import { STTService, TranscriptionResult } from "../types/stt";
import { GoogleGenerativeAI } from "@google/generative-ai";

export class GoogleSTTService implements STTService {
  private client: GoogleGenerativeAI;
  private model: string;
  private _type: "transcribe" | "translate";

  constructor(
    apiKey: string,
    model: string = "gemini-2.0-flash",
    type: "transcribe" | "translate" = "transcribe"
  ) {
    this.client = new GoogleGenerativeAI(apiKey);
    this.model = model;
    this._type = type;
  }

  get type(): "transcribe" | "translate" {
    return this._type;
  }

  get name() {
    let modelName;
    switch (this.model) {
      case "gemini-2.0-flash":
        modelName = "Gemini 2.0 Flash";
        break;
      case "gemini-2.0-flash-lite":
        modelName = "Gemini 2.0 Flash Lite";
        break;
      case "gemini-2.5-pro-exp-03-25":
        modelName = "Gemini 2.5 Pro Experimental";
        break;
      default:
        modelName = this.model;
    }
    return `Google ${modelName} ${
      this._type === "translate" ? "Translate" : "Transcribe"
    }`;
  }

  private async convertToWav(audioBlob: Blob): Promise<ArrayBuffer> {
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    // Read the blob as array buffer
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Create offline context for resampling
    const offlineContext = new OfflineAudioContext({
      numberOfChannels: 1,
      length: Math.ceil(audioBuffer.duration * 16000),
      sampleRate: 16000,
    });

    // Create buffer source
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start();

    // Render audio
    const renderedBuffer = await offlineContext.startRendering();

    // Convert to 16-bit PCM WAV
    const length = renderedBuffer.length * 2; // 16-bit samples
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    // Write WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, "RIFF"); // RIFF identifier
    view.setUint32(4, 36 + length, true); // File length
    writeString(8, "WAVE"); // WAVE identifier
    writeString(12, "fmt "); // Format chunk identifier
    view.setUint32(16, 16, true); // Format chunk length
    view.setUint16(20, 1, true); // Sample format (1 = PCM)
    view.setUint16(22, 1, true); // Channel count
    view.setUint32(24, 16000, true); // Sample rate
    view.setUint32(28, 16000 * 2, true); // Byte rate (sample rate * block align)
    view.setUint16(32, 2, true); // Block align (channel count * bytes per sample)
    view.setUint16(34, 16, true); // Bits per sample
    writeString(36, "data"); // Data chunk identifier
    view.setUint32(40, length, true); // Data chunk length

    // Write audio data
    const samples = renderedBuffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < samples.length; i++) {
      const sample = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(
        offset,
        sample < 0 ? sample * 0x8000 : sample * 0x7fff,
        true
      );
      offset += 2;
    }

    return buffer;
  }

  private async convertAudioToBase64(audioBlob: Blob): Promise<string> {
    // First convert to WAV format
    const wavBuffer = await this.convertToWav(audioBlob);
    // Then convert to base64
    const uint8Array = new Uint8Array(wavBuffer);
    const binaryString = uint8Array.reduce(
      (str, byte) => str + String.fromCharCode(byte),
      ""
    );
    return btoa(binaryString);
  }

  async transcribe(
    audioBlob: Blob,
    forceType?: "transcribe" | "translate"
  ): Promise<TranscriptionResult> {
    const startTime = Date.now();

    try {
      const audioBase64 = await this.convertAudioToBase64(audioBlob);
      const model = this.client.getGenerativeModel({ model: this.model });

      // Use forceType if provided, otherwise use instance type
      const currentType = forceType || this._type;

      // Prepare the prompt based on the type
      let prompt = "";
      if (currentType === "translate") {
        prompt = `IMPORTANT: You are a professional translator specializing in business/economics content. Your task is to TRANSLATE the audio to polished, natural English while maintaining speaker identification.

CRITICAL INSTRUCTIONS:
1. **Maintain speaker identification** throughout the translation
2. **Remove filler words** and speech disfluencies
3. **Restructure fragmented speech** into cohesive, logical sentences
4. **Preserve statistics exactly**
5. **Use formal business English tone** (suitable for analyst reports)
6. **Format rules:**
   - If you can identify the speaker's name, use it (e.g., "John: [English translation]")
   - If you cannot identify the speaker's name, use letters (e.g., "Speaker A: [English translation]")
   - Combine consecutive sentences from the same speaker into a single block
   - Only add a new speaker label when there's a change in who is speaking
7. Output ONLY the English translation with speaker labels

REMEMBER: Output ONLY the English translation with speaker labels, no explanations or notes.`;
      } else {
        prompt = `Task: Transcribe the audio in its original language with speaker identification.

Rules:
- Keep the exact original language (do not translate)
- Preserve all characters and punctuation as spoken
- Identify and label each speaker consistently throughout the transcription
- If you can identify the speaker's name, use it (e.g., "John: Hello there. How are you today?")
- If you cannot identify the speaker's name, use letters (e.g., "Speaker A: Hello there. How are you today?")
- Maintain the same speaker labels throughout the entire transcription
- Combine consecutive sentences from the same speaker into a single block - do not repeat the speaker label if they continue speaking
- Only add a new speaker label when there's a change in who is speaking
- No explanations or notes
- Output only the transcription with speaker labels`;
      }

      const result = await model.generateContent({
        contents: [
          {
            role: "user",
            parts: [
              { text: prompt },
              { inlineData: { mimeType: "audio/wav", data: audioBase64 } },
            ],
          },
        ],
        generationConfig: {
          temperature: 0.1,
          topP: 0.1,
          topK: 1,
        },
      });

      const response = await result.response;
      const text = response.text();
      let cleanedText = text.trim();

      // For translations, clean up the text
      if (currentType === "translate") {
        cleanedText = cleanedText
          .replace(
            /^(Response:|Translation:|Output:|Translated text:|English translation:)\s*/i,
            ""
          )
          .trim();
      }

      const duration = (Date.now() - startTime) / 1000;

      return {
        text: cleanedText,
        duration,
        provider: "google",
        model: this.model,
        type: currentType,
        processingTime: duration,
        isTextTranslation: false,
      };
    } catch (error) {
      console.error("Google STT error:", error);
      if (error instanceof Error) {
        throw new Error(`Google STT error: ${error.message}`);
      }
      throw new Error("Unknown Google STT error occurred");
    }
  }

  async translate(audioBlob: Blob): Promise<TranscriptionResult> {
    // Instead of modifying this._type, we pass the type as a parameter
    return this.transcribe(audioBlob, "translate");
  }

  async translateText(
    text: string
  ): Promise<{ text: string; duration: number }> {
    const startTime = Date.now();

    try {
      const model = this.client.getGenerativeModel({ model: this.model });

      const prompt = `IMPORTANT: You are a professional translator specializing in business/economics content. Your task is to TRANSLATE the text to polished, natural English while maintaining speaker identification.

CRITICAL INSTRUCTIONS:
1. **Maintain speaker identification** throughout the translation
2. **Remove filler words** and speech disfluencies
3. **Restructure fragmented speech** into cohesive, logical sentences
4. **Preserve statistics exactly**
5. **Use formal business English tone** (suitable for analyst reports)
6. **Format rules:**
   - If you can identify the speaker's name, use it (e.g., "John: [English translation]")
   - If you cannot identify the speaker's name, use letters (e.g., "Speaker A: [English translation]")
   - Combine consecutive sentences from the same speaker into a single block
   - Only add a new speaker label when there's a change in who is speaking
7. Output ONLY the English translation with speaker labels

Text to translate: "${text}"

REMEMBER: Output ONLY the English translation with speaker labels, no explanations or notes.`;

      const result = await model.generateContent(prompt);

      const response = await result.response;
      const translatedText = response.text();

      // Clean up the response
      const cleanedText = translatedText
        .replace(
          /^(Response:|Translation:|Output:|Translated text:|English translation:)\s*/i,
          ""
        )
        .replace(/[^\x00-\x7F]/g, "")
        .replace(/\([^)]*\)/g, "")
        .replace(/^["']|["']$/g, "")
        .trim();

      const duration = (Date.now() - startTime) / 1000;

      return {
        text: cleanedText,
        duration,
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Google translation error: ${error.message}`);
      }
      throw new Error("Unknown translation error occurred");
    }
  }
}
